import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Input, Button, Spinner, Alert, Table, Row, Col } from 'reactstrap';

const FileUpload = ({ isModal, isOpen, showFooterButtons, title = "Upload", requiredFileExtensions, displayTableData = true, onFileUpload, onToggle, onImport }) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState({ headers: [], rows: [] });
    const [error, setError] = useState('');
    const [isUploadButtonEnabled, setIsUploadButtonEnabled] = useState(false);
    const [isImportButtonEnabled, setIsImportButtonEnabled] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen]);

    const resetState = () => {
        setFile(null);
        setLoading(false);
        setTableData({ headers: [], rows: [] });
        setError('');
        setIsImportButtonEnabled(false);
        setIsUploadButtonEnabled(false);
    }

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (requiredFileExtensions) {
            const fileExtensions = requiredFileExtensions.split(",").map(ext => ext.trim().toLowerCase());
            if (!fileExtensions.some(ext => selectedFile.name.toLowerCase().endsWith(ext))) {
                setFile(null);
                setError(`Only ${fileExtensions.join(" ")} files are allowed.`);
                setTableData({ headers: [], rows: [] });
                return;
            }
        }

        const formData = new FormData();
        formData.set('file', selectedFile);
        setFile(formData);
        setError('');
        setIsUploadButtonEnabled(true);
        setTableData({ headers: [], rows: [] });
        e.target.value = '';
    };

    function extractHeaders(data) {
        const headersSet = new Set();
        data.forEach(row => {
            Object.keys(row).forEach(key => headersSet.add(key));
        });
        return Array.from(headersSet);
    }

    const handleUpload = async () => {
        if (!file) return;

        setLoading(true);
        setIsUploadButtonEnabled(false);
        setIsImportButtonEnabled(false);
        setError('');

        try {
            if (onFileUpload != null) {
                // onFileUpload is an optional method prop passed from the parent component
                // it receives the file as a parameter and can do anything with it. For instance, working out the file data and returning a json array to display in a table
                let data = await onFileUpload(file);
                if (data.error !== undefined) {
                    setTableData({ headers: [], rows: [] });
                    setError(data.error);
                    return
                }
                else if (displayTableData && data) { // display the returned data on a table
                    const showValidationErrors = data.showValidationErrors
                    data = showValidationErrors ? data.validationErrors : data;
                    if (Array.isArray(data) && data.length > 0) {
                        const headers = extractHeaders(data);
                        setTableData({ headers, rows: data });
                        if (!showValidationErrors) setIsImportButtonEnabled(true);
                    } else {
                        setTableData({ headers: [], rows: [] });
                        setError('No data to display.');
                        return
                    }
                }
            }
        } catch (err) {
            console.error(err);
            setError('Upload or parsing failed.');
        } finally {
            setLoading(false);
        }
    };

    const onImportClick = () => {
        onToggle();
        onImport();
    }

    return (
        <div className="container mt-4">
            <style>
                {`
        thead th {
          position: sticky;
          top: 0;  
          z-index: 100 !important;
          
        }
        .custom-modal-size {
            max-width: 95vw  !important;
            width: 95vw !important;           
            margin: 0 auto;
          }
          .custom-modal-size .modal-content {
            height: 90vh !important;
          }
        `}
            </style>

            {isModal ? (
                <Modal isOpen={isOpen} toggle={onToggle} centered size="xl" scrollable backdrop="static" className="custom-modal-size">
                    <ModalHeader toggle={onToggle}>{title}</ModalHeader>
                    <ModalBody>
                        {renderContent()}
                    </ModalBody>
                    {showFooterButtons &&
                        <ModalFooter>
                            <Button color="primary" onClick={onToggle}>
                                Cancel
                            </Button>
                            <Button color="secondary" style={{ display: isImportButtonEnabled ? "block" : "none" }} onClick={onImportClick}>
                                Import
                            </Button>
                        </ModalFooter>
                    }
                </Modal>
            ) : (
                <div>
                    {renderContent()}
                    {showFooterButtons &&
                        <div className="mt-3 d-flex justify-content-end">
                            <Button color="primary" onClick={onToggle} className="me-2">
                                Cancel
                            </Button>
                            <Button color="secondary" style={{ display: isImportButtonEnabled ? "block" : "none" }} onClick={onImportClick}>
                                Import
                            </Button>
                        </div>
                    }
                </div>
            )}
        </div>
    );

    function renderContent() {
        return (
            <>
                <FormGroup>
                    <Row className="align-items-center">
                        <Col className="col-auto d-flex align-items-center gap-2">
                            <label
                                htmlFor="fileInput"
                                className="btn"
                                style={{ backgroundColor: 'white', color: 'black', border: '1px solid #ced4da' }}
                            >
                                {file ? 'Change file' : 'Choose file'}
                            </label>
                            <Input
                                id="fileInput"
                                type="file"
                                style={{ display: 'none' }}
                                {...(requiredFileExtensions ? { accept: requiredFileExtensions } : {})}
                                onChange={handleFileChange}
                            />
                            {file && (
                                <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    <strong>{file.get('file')?.name}</strong>
                                </span>
                            )}
                        </Col>
                        {file && !loading && (
                            <Col className="col-auto">
                                {isUploadButtonEnabled && (<Button color="primary" onClick={handleUpload}>Upload</Button>)}
                            </Col>
                        )}
                    </Row>
                </FormGroup>

                {error && (
                    <Alert color="danger">
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            {error}
                            <Button outline color="dark" onClick={() => setError("")}>
                                x
                            </Button>
                        </div>
                    </Alert>
                )}

                {loading && (
                    <div className="text-center mt-3">
                        <Spinner color="primary" />
                        <p className="text-muted mt-2">Uploading and parsing...</p>
                    </div>
                )}

                {displayTableData && tableData.headers.length > 0 && (
                    <div className="mt-4">
                        <h6>Preview:</h6>
                        <div style={{ maxHeight: "570px", overflowY: "auto", overflowX: "auto", position: "relative" }}>
                            <Table bordered hover size="sm" className="m-0">
                                <thead className="table-light">
                                    <tr>
                                        {tableData.headers.map((header, index) => (
                                            <th key={index}>{header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.rows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {tableData.headers.map((header, colIndex) => (
                                                <td key={colIndex}>
                                                    {row[header] !== null && row[header] !== undefined
                                                        ? row[header].toString()
                                                        : row[header]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </>
        );
    }

};

export default FileUpload;

